import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon, Who } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Drawer, Summary, Tabs, EmptyState } from './Overlay';
import { STATUS_META } from './BookingList';

/*
 * Customers — arketipe B paket client (`customers` di screens.js).
 *
 * Kolom, filter, aksi baris, dan drawer dua tabnya mengikuti prototipe. Yang
 * berbeda dari prototipe cuma satu hal yang memang tidak bisa sama: di sana
 * "Bookings" adalah angka karangan, di sini jumlah booking sungguhan dan
 * "Lifetime spend" hanya menjumlahkan yang berstatus completed.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String, $banStatus: String) {
  getCustomers(currentPage: $currentPage, limit: $limit, searchList: $searchList, banStatus: $banStatus) {
    count status errorMessage
    results { id name email phone bookings completed spend status joined lastBookingAt }
  }
}`;

// Booking milik satu pelanggan untuk tab kedua drawer. Lewat argumen userId,
// bukan searchList — searchFilter mencocokkan firstName dan id booking, jadi
// UUID pelanggan tidak pernah cocok dengan apa pun di sana.
const Q_THEIRS = `query ($userId: String) {
  getAllBookings(currentPage: 1, limit: 25, userId: $userId) {
    status
    results { id status totalFare currency createdAt categoryDetails { name } }
  }
}`;

const M_BAN = `mutation ($userId: String!, $ban: Boolean!, $reason: String, $note: String) {
  setCustomerBan(userId: $userId, ban: $ban, reason: $reason, note: $note) { status errorMessage }
}`;

// Dari prototipe: reasonOptions pada banCustomer.
const BAN_REASONS = ['Payment fraud', 'Abusive behaviour', 'Repeated no-shows',
  'Fake bookings', 'Chargeback abuse', 'Other'];

const copy = locale => locale === 'ar' ? {
  people: 'الأشخاص', customers: 'العملاء', account: 'حساب مسجل', accounts: 'حسابات مسجلة',
  export: 'تصدير CSV', preparing: 'جارٍ التجهيز…', classic: 'فتح النسخة الكلاسيكية',
  loadError: 'تعذر تحميل العملاء.', exportError: 'تعذر تصدير العملاء.',
  customer: 'العميل', unnamed: 'بدون اسم', phone: 'الهاتف', bookings: 'الحجوزات',
  spend: 'إجمالي الإنفاق', status: 'الحالة', joined: 'تاريخ الانضمام', active: 'نشط', banned: 'محظور',
  view: 'عرض ملف', ban: 'حظر العميل', unban: 'رفع الحظر', overview: 'نظرة عامة', close: 'إغلاق',
  customerId: 'معرّف العميل', email: 'البريد الإلكتروني', lastBooking: 'آخر حجز', never: 'لا يوجد',
  lifetimeBookings: 'إجمالي الحجوزات', completed: 'المكتملة', perCompleted: 'لكل خدمة مكتملة. يشمل الإنفاق الحجوزات المكتملة فقط.',
  loading: 'جارٍ التحميل…', bookingLoadError: 'تعذر تحميل حجوزات العميل.', noBookings: 'لا توجد حجوزات',
  noBookingsMsg: 'لدى هذا العميل حساب ولكنه لم ينشئ أي حجز.', ref: 'المرجع', service: 'الخدمة', fare: 'التعرفة',
  search: 'ابحث بالاسم أو الهاتف أو البريد أو المعرّف', allStatuses: 'الحالة: الكل',
  empty: 'لا يوجد عملاء بعد', emptyHint: 'تظهر الحسابات هنا عند التسجيل في التطبيق.',
  dismiss: 'إخفاء', reason: 'سبب الحظر', chooseReason: 'اختر سبباً…', reasonRequired: 'السبب مطلوب ويُسجل في سجل التدقيق.',
  adminNote: 'ملاحظة المسؤول (اختيارية)', notePlaceholder: 'أضف تفاصيل مفيدة لسجل التدقيق…',
  banTitle: name => `حظر ${name}؟`, banSub: 'لن يتمكن من إنشاء حجوزات جديدة.',
  unbanTitle: name => `رفع الحظر عن ${name}؟`, unbanSub: 'سيتمكن من الحجز مجدداً فوراً.',
  banSuccess: (name, reason) => `تم حظر ${name} — ${reason}. لن يتمكن من الحجز.`,
  unbanSuccess: name => `تم رفع الحظر — يمكن لـ ${name} الحجز مجدداً.`,
  banError: 'تعذر حظر العميل.', unbanError: 'تعذر رفع الحظر.', customerRole: 'عميل',
  showing: (from, to, total) => `عرض ${from}–${to} من ${total}`,
  sortedPage: 'الترتيب داخل هذه الصفحة', previousPage: 'الصفحة السابقة', nextPage: 'الصفحة التالية',
  rowsPerPage: 'عدد الصفوف في الصفحة', perPage: n => `${n} / صفحة`, rtl: true,
  reasons: {
    'Payment fraud': 'احتيال في الدفع', 'Abusive behaviour': 'سلوك مسيء',
    'Repeated no-shows': 'تكرار عدم الحضور', 'Fake bookings': 'حجوزات وهمية',
    'Chargeback abuse': 'إساءة استخدام استرداد المدفوعات', Other: 'أخرى'
  }
} : {
  people: 'People', customers: 'Customers', account: 'registered account', accounts: 'registered accounts',
  export: 'Export CSV', preparing: 'Preparing…', classic: 'Open in classic',
  loadError: 'Could not load customers.', exportError: 'Could not export customers.',
  customer: 'Customer', unnamed: 'Unnamed', phone: 'Phone', bookings: 'Bookings',
  spend: 'Lifetime spend', status: 'Status', joined: 'Joined', active: 'Active', banned: 'Banned',
  view: 'View profile', ban: 'Ban customer', unban: 'Lift ban', overview: 'Overview', close: 'Close',
  customerId: 'Customer ID', email: 'Email', lastBooking: 'Last booking', never: 'Never',
  lifetimeBookings: 'Lifetime bookings', completed: 'Completed', perCompleted: 'per completed job. Spend counts completed bookings only.',
  loading: 'Loading…', bookingLoadError: 'Could not load customer bookings.', noBookings: 'No bookings yet',
  noBookingsMsg: 'This customer has an account but has never booked.', ref: 'Ref', service: 'Service', fare: 'Fare',
  search: 'Search name, phone, email or ID', allStatuses: 'Status: all',
  empty: 'No customers yet', emptyHint: 'Accounts appear here as people sign up in the app.',
  dismiss: 'Dismiss', reason: 'Ban reason', chooseReason: 'Choose a reason…', reasonRequired: 'A reason is required. It is written to the audit log.',
  adminNote: 'Admin note (optional)', notePlaceholder: 'Add useful context for the audit log…',
  banTitle: name => `Ban ${name}?`, banSub: 'They will not be able to place new bookings.',
  unbanTitle: name => `Lift the ban on ${name}?`, unbanSub: 'They will be able to book again immediately.',
  banSuccess: (name, reason) => `${name} banned — ${reason}. They can no longer book.`,
  unbanSuccess: name => `Ban lifted — ${name} can book again.`,
  banError: 'Could not ban.', unbanError: 'Could not lift the ban.', customerRole: 'customer',
  showing: (from, to, total) => `Showing ${from}–${to} of ${Number(total).toLocaleString('en-US')}`,
  sortedPage: 'sorted within this page', previousPage: 'Previous page', nextPage: 'Next page',
  rowsPerPage: 'Rows per page', perPage: n => `${n} / page`, rtl: false,
  reasons: BAN_REASONS.reduce((out, reason) => ({ ...out, [reason]: reason }), {})
};

const fmt = (n, locale) => Number(n || 0).toLocaleString(locale === 'ar' ? 'ar-AE' : 'en-US');
const money = (n, locale) => `AED ${fmt(Math.round(Number(n) || 0), locale)}`;
const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? '—' : d.toISOString().slice(0, 10);
};

const BanTag = ({ status, t }) => (
  <span className={`tag ${status === 'banned' ? 'danger' : 'ok'}`}>
    <i />{status === 'banned' ? t.banned : t.active}
  </span>
);

class Customers extends React.Component {
  state = {
    rows: [], count: 0, page: 1, perPage: 10, search: '', banStatus: '',
    loading: true, error: null, exporting: false,
    viewing: null, tab: 0, theirs: null,
    banning: null, unbanning: null, reason: '', note: '', saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  t = () => copy(this.props.intl.locale);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    banStatus: this.state.banStatus || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getCustomers;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.t().loadError });
      return;
    }
    this.setState({ rows: res.results || [], count: res.count || 0, loading: false });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  setBan = e => this.setState({ banStatus: e.target.value, page: 1 }, () => this.load());

  exportCsv = async () => {
    this.setState({ exporting: true });
    const json = await gql(Q, this.vars({ currentPage: 1, limit: 1000 })).catch(() => null);
    const res = json && json.data && json.data.getCustomers;
    this.setState({ exporting: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: this.t().exportError } });
      return;
    }

    const cell = v => '"' + String(v === null || v === undefined ? '' : v).replace(/"/g, '""') + '"';
    const t = this.t();
    const rows = [
      [t.customerId, t.customer, t.email, t.phone, t.bookings, t.completed, `${t.spend} AED`, t.status, t.joined],
      ...res.results.map(r => [
        r.id, r.name || '', r.email || '', r.phone || '', r.bookings, r.completed,
        Math.round(r.spend), r.status === 'banned' ? t.banned : t.active, day(r.joined)
      ])
    ];
    const url = URL.createObjectURL(
      new Blob([rows.map(r => r.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bas-customers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  view = async c => {
    this.setState({ viewing: c, tab: 0, theirs: null });
    const json = await gql(Q_THEIRS, { userId: c.id }).catch(() => null);
    const res = json && json.data && json.data.getAllBookings;
    this.setState({ theirs: res && res.status === 200 ? (res.results || []) : false });
  };

  ban = async () => {
    const { banning, reason, note } = this.state;
    if (!banning || !reason) return;
    this.setState({ saving: true });
    const json = await gql(M_BAN, {
      userId: banning.id, ban: true, reason,
      note: note.trim() || undefined
    }).catch(() => null);
    const res = json && json.data && json.data.setCustomerBan;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.t().banError } });
      return;
    }
    this.setState({
      banning: null, reason: '', note: '',
      notice: { tone: 'danger', text: this.t().banSuccess(banning.name || banning.email, this.t().reasons[reason] || reason) }
    });
    this.load(true);
  };

  liftBan = async () => {
    const { unbanning } = this.state;
    if (!unbanning) return;
    this.setState({ saving: true });
    const json = await gql(M_BAN, { userId: unbanning.id, ban: false }).catch(() => null);
    const res = json && json.data && json.data.setCustomerBan;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.t().unbanError } });
      return;
    }
    this.setState({
      unbanning: null,
      notice: { tone: 'amber', text: this.t().unbanSuccess(unbanning.name || unbanning.email) }
    });
    this.load(true);
  };

  columns() {
    const t = this.t();
    const locale = this.props.intl.locale;
    return [
      {
        key: 'name', label: t.customer, sortable: true,
        render: r => <Who name={r.name || t.unnamed} sub={r.id.slice(0, 8)} />
      },
      { key: 'phone', label: t.phone, render: r => <span className="mono">{r.phone || '—'}</span> },
      { key: 'bookings', label: t.bookings, align: 'end', sortable: true },
      {
        key: 'spend', label: t.spend, align: 'end', sortable: true,
        render: r => <span className="num">{r.spend ? money(r.spend, locale) : '—'}</span>
      },
      { key: 'status', label: t.status, render: r => <BanTag status={r.status} t={t} /> },
      {
        key: 'joined', label: t.joined, align: 'end',
        sortValue: r => r.joined || '',
        render: r => <span className="mono">{day(r.joined)}</span>
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title={t.view}
              aria-label={`${t.view} ${r.name || r.email}`}
              onClick={() => this.view(r)}><Icon name="eye" /></button>
            <button className="btn icon" type="button"
              title={r.status === 'active' ? t.ban : t.unban}
              aria-label={`${r.status === 'active' ? t.ban : t.unban} ${r.name || r.email}`}
              onClick={() => (r.status === 'active'
                ? this.setState({ banning: r, reason: '', note: '', notice: null })
                : this.setState({ unbanning: r, notice: null }))}>
              <Icon name={r.status === 'active' ? 'x' : 'check'} />
            </button>
          </span>
        )
      }
    ];
  }

  renderDrawer() {
    const { viewing: c, tab, theirs } = this.state;
    const t = this.t();
    const locale = this.props.intl.locale;
    const avg = c.completed ? money(c.spend / c.completed, locale) : '—';

    return (
      <Drawer
        title={c.name || t.unnamed}
        sub={`${c.id.slice(0, 8)} · ${t.customerRole}`}
        headExtra={<BanTag status={c.status} t={t} />}
        tabs={<Tabs labels={[t.overview, t.bookings]} active={tab}
          onChange={i => this.setState({ tab: i })} />}
        onClose={() => this.setState({ viewing: null, theirs: null })}
        actions={
          <div className="sp">
            <button className="btn ghost" type="button"
              onClick={() => this.setState({ viewing: null, theirs: null })}>{t.close}</button>
            {c.status === 'active'
              ? <button className="btn ghost" type="button"
                  onClick={() => this.setState({ viewing: null, banning: c, reason: '', note: '' })}>{t.ban}</button>
              : <button className="btn" type="button"
                  onClick={() => this.setState({ viewing: null, unbanning: c })}>{t.unban}</button>}
          </div>
        }
      >
        {tab === 0 && (
          <div>
            <Summary pairs={[
              [t.customerId, <span className="mono">{c.id}</span>],
              [t.phone, <span className="mono">{c.phone || '—'}</span>],
              [t.email, c.email || '—'],
              [t.status, <BanTag status={c.status} t={t} />],
              [t.joined, day(c.joined)],
              [t.lastBooking, c.lastBookingAt ? day(c.lastBookingAt) : t.never],
              [t.lifetimeBookings, String(c.bookings)],
              [t.completed, String(c.completed)],
              [t.spend, money(c.spend, locale)]
            ]} />
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)' }}>
              {avg} {t.perCompleted}
            </p>
          </div>
        )}

        {tab === 1 && (
          theirs === null
            ? <p style={{ color: 'var(--tx-3)' }}>{t.loading}</p>
            : theirs === false
              ? <div className="notice danger"><span className="ni">!</span><span className="nt">{t.bookingLoadError}</span></div>
            : theirs.length === 0
              ? <EmptyState icon="inbox" title={t.noBookings} msg={t.noBookingsMsg} />
              : (
                /* min-width tabel utama 680px, lebih lebar dari badan drawer,
                   jadi kolom Fare terpotong keluar layar. Empat kolom ini muat
                   tanpa itu. */
                <table className="tbl" style={{ minWidth: 0 }}>
                  <thead>
                    <tr><th>{t.ref}</th><th>{t.service}</th><th>{t.status}</th><th className="right">{t.fare}</th></tr>
                  </thead>
                  <tbody>
                    {theirs.map(b => {
                      const [label, kind] = STATUS_META[b.status] || [b.status, ''];
                      return (
                        <tr key={b.id}>
                          <td><span className="mono">BK-{b.id}</span></td>
                          <td>{(b.categoryDetails || {}).name || '—'}</td>
                          <td><span className={`tag ${kind}`}><i />{label}</span></td>
                          <td className="right">
                            <span className="num">{b.totalFare ? money(b.totalFare, locale) : '—'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
        )}
      </Drawer>
    );
  }

  render() {
    const {
      rows, count, page, perPage, loading, error, exporting, banStatus,
      viewing, banning, unbanning, reason, note, saving, notice
    } = this.state;
    const t = this.t();

    return (
      <AdminShell
        current="customers"
        breadcrumb={[t.people, t.customers]}
        title={t.customers}
        lead={`${fmt(count, this.props.intl.locale)} ${count === 1 ? t.account : t.accounts}.`}
        actions={[
          <button className="btn ghost" type="button" key="x"
            onClick={this.exportCsv} disabled={exporting || !count}>
            <Icon name="dl" />{exporting ? t.preparing : t.export}
          </button>,
          <a className="btn ghost" href="/siteadmin/users" key="c">{t.classic}</a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t.dismiss}</button>
          </div>
        )}

        {viewing && this.renderDrawer()}

        {banning && (
          <Modal
            icon="x" tone="danger"
            title={t.banTitle(banning.name || banning.email)}
            sub={t.banSub}
            confirmLabel={t.ban}
            busy={saving}
            disabled={!reason}
            onConfirm={this.ban}
            onClose={() => this.setState({ banning: null, reason: '', note: '' })}
          >
            <div className="field">
              <label>{t.reason}</label>
              <select className="inp" value={reason}
                onChange={e => this.setState({ reason: e.target.value })}>
                <option value="">{t.chooseReason}</option>
                {BAN_REASONS.map(r => <option key={r} value={r}>{t.reasons[r]}</option>)}
              </select>
              {!reason && <span className="hint">{t.reasonRequired}</span>}
            </div>
            <div className="field">
              <label>{t.adminNote}</label>
              <textarea className="inp" rows="3" maxLength="2000" value={note}
                placeholder={t.notePlaceholder}
                onChange={e => this.setState({ note: e.target.value })} />
            </div>
          </Modal>
        )}

        {unbanning && (
          <Modal
            icon="check"
            title={t.unbanTitle(unbanning.name || unbanning.email)}
            sub={t.unbanSub}
            confirmLabel={t.unban}
            busy={saving}
            onConfirm={this.liftBan}
            onClose={() => this.setState({ unbanning: null })}
          />
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t.search}
              copy={t}
              filters={
                <select className="inp" value={banStatus} onChange={this.setBan}
                  aria-label="Filter by status">
                  <option value="">{t.allStatuses}</option>
                  <option value="active">{t.active}</option>
                  <option value="banned">{t.banned}</option>
                </select>
              }
              emptyText={t.empty}
              emptyHint={t.emptyHint}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(Customers);
